import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Popper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  RestartAlt as RestartAltIcon,
  DeleteOutline as DeleteOutlineIcon,
  ChevronLeft,
  ChevronRight,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import Hls from 'hls.js';
import {
  authenticatedFetch,
  isRecordingHlsStreamable,
  isRecordingWebPlayable,
  recordingTranscodingBlockedTooltip,
} from '../utils/api';
import { buildApiUrl } from '../config/api';

const DAY_MINUTES = 24 * 60;
const ZOOM_MIN_SPAN_MINS = 30;
const ZOOM_MAX_SPAN_MINS = DAY_MINUTES;
const ZOOM_WHEEL_FACTOR = 1.12;
const SLOT_GREEN = '#bbf7d0';
const RECORDING_ENTRY = '#15803d';
const RECORDING_EXIT = '#2563eb';
/** Distinct fills for CAM_* tracks (entry / exit stay fixed green / blue). */
const EXTRA_CAMERA_FILL_PALETTE = [
  '#7c3aed',
  '#c2410c',
  '#0d9488',
  '#ca8a04',
  '#db2777',
  '#65a30d',
  '#4f46e5',
  '#be123c',
];
/** End-of-recording edge dots (start dots use the active track recording color). */
const RECORDING_END_DOT = '#0f172a';
const TRACK_GREY = '#e5e7eb';
const EDGE_MARKER_PCT_DECIMALS = 3;
/** Must match the track row so hour labels / minimap share the same horizontal scale as segments. */
const TRACK_SIDE_LABEL_WIDTH_PX = 100;

/**
 * When two recordings overlap on the timeline (often bad duration/end on an earlier clip),
 * prefer the one that started latest — that is usually the clip that actually covers the instant.
 */
function pickRecordingSegmentAtMinute(recordingSegments, minute) {
  if (!recordingSegments?.length || minute == null || !Number.isFinite(minute)) return null;
  const matches = recordingSegments.filter(
    ({ startMin, endMin }) => minute >= startMin && minute <= endMin
  );
  if (!matches.length) return null;
  return matches.reduce((best, seg) => (seg.startMin > best.startMin ? seg : best));
}

const extractStreamTokenFromPayload = (data) => {
  if (data?.token) return data.token;
  const url = data?.url || '';
  const qIndex = url.indexOf('?');
  if (qIndex === -1) return null;
  return new URLSearchParams(url.slice(qIndex + 1)).get('token');
};

const HLS_MAX_BUFFER_SECONDS = 15;
const HLS_MAX_MAX_BUFFER_SECONDS = 30;
const HLS_BACK_BUFFER_SECONDS = 10;

const formatSize = (bytes) => {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDurationHuman = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const s = Math.floor(Number(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

/** SQL / legacy: `2026-03-27 09:00:00` → ISO-like `T` so browsers parse as local wall time consistently. */
const normalizeInstantString = (s) => {
  const t = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}/.test(t)) return t.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T');
  return t;
};

/**
 * Parse recording instants like the table (`new Date(...)`), after normalizing space → T.
 * Do not append Z to unqualified strings — that forces UTC and shifts wall time.
 */
const parseRecordingDate = (iso) => {
  if (iso == null) return null;
  const trimmed = String(iso).trim();
  if (!trimmed) return null;
  const d = new Date(normalizeInstantString(trimmed));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Local midnight for calendar dateKey `yyyy-MM-dd` (unambiguous in the browser TZ). */
const localMidnightForDateKey = (dateKey) => {
  const parts = String(dateKey).split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const mo = Number(parts[1]);
  const da = Number(parts[2]);
  if (!y || !mo || !da) return null;
  const d = new Date(y, mo - 1, da, 0, 0, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

const FILENAME_TIME_RE = /(?:IN|OUT)[_-](\d{1,2})-([A-Za-z]{3})-(\d{4})_(\d{1,2})-(\d{2})(AM|PM)/i;
/** CAM_<id>_12-Apr-2026_09-00AM_… and any other prefix before the date token. */
const FILENAME_TIME_GENERIC_RE = /(\d{1,2})-([A-Za-z]{3})-(\d{4})_(\d{1,2})-(\d{2})(AM|PM)/i;
const MONTH_ABBR = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/**
 * Start minutes from local midnight using embedded file time (e.g. IN_27-Mar-2026_09-00AM_…).
 * Only when file date matches `dateKey`.
 */
const parseFilenameStartMinutes = (filename, dateKey) => {
  if (!filename || !dateKey) return null;
  let m = String(filename).match(FILENAME_TIME_RE);
  if (!m) m = String(filename).match(FILENAME_TIME_GENERIC_RE);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const monStr = m[2].toLowerCase().slice(0, 3);
  const year = parseInt(m[3], 10);
  let hour = parseInt(m[4], 10);
  const minute = parseInt(m[5], 10);
  const ap = m[6].toUpperCase();
  const monthIdx = MONTH_ABBR[monStr];
  if (monthIdx === undefined || Number.isNaN(day) || Number.isNaN(year) || Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  const mk = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  if (mk !== dateKey) return null;
  if (ap === 'PM' && hour !== 12) hour += 12;
  if (ap === 'AM' && hour === 12) hour = 0;
  const midnight = localMidnightForDateKey(dateKey);
  if (!midnight) return null;
  const inst = new Date(year, monthIdx, day, hour, minute, 0, 0);
  if (Number.isNaN(inst.getTime())) return null;
  return (inst.getTime() - midnight.getTime()) / 60000;
};

/** Calendar day in local timezone (matches API date filter + list). */
const localDateKeyFromInstant = (iso) => {
  const d = parseRecordingDate(iso);
  if (!d) return null;
  return format(d, 'yyyy-MM-dd');
};

/**
 * Exact minutes from local midnight on `dateKey` (same instant the API calls recording start).
 * Uses `recorded_at` wall time vs unambiguous local midnight for that calendar day.
 */
const minutesIntoLocalCalendarDay = (iso, dateKey) => {
  const d = parseRecordingDate(iso);
  if (!d || !dateKey) return null;
  if (format(d, 'yyyy-MM-dd') !== dateKey) return null;
  const midnight = localMidnightForDateKey(dateKey);
  if (!midnight) return null;
  return (d.getTime() - midnight.getTime()) / 60000;
};

const isEntryFilename = (filename) => {
  const u = (filename || '').toString().trim().toUpperCase();
  return u.startsWith('IN');
};

const isExitFilename = (filename) => {
  const u = (filename || '').toString().trim().toUpperCase();
  return u.includes('OUT');
};

/** Stable track key for grouping recordings on the timeline. */
export const recordingTrackKeyFromRec = (rec) => {
  const cid = rec?.camera_config_id;
  if (cid && String(cid).trim()) return `cam:${String(cid).trim()}`;
  if (isEntryFilename(rec?.filename)) return 'legacy:in';
  if (isExitFilename(rec?.filename)) return 'legacy:out';
  return 'legacy:other';
};

const hashStringHue = (s) => {
  let h = 0;
  const str = String(s || '');
  for (let i = 0; i < str.length; i += 1) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return ((h % 360) + 360) % 360;
};

/** Solid fill for recordings / markers for this track key. */
export const recordingFillForTrackKey = (key) => {
  if (key === 'legacy:in') return RECORDING_ENTRY;
  if (key === 'legacy:out') return RECORDING_EXIT;
  if (key === 'legacy:other') return '#64748b';
  if (key.startsWith('cam:')) {
    const id = key.slice(4);
    const idx = Math.abs(hashStringHue(id)) % EXTRA_CAMERA_FILL_PALETTE.length;
    return EXTRA_CAMERA_FILL_PALETTE[idx];
  }
  return '#64748b';
};

/** Label for a timeline / table section (stable from track key, not only the first row). */
export const recordingLabelForTrackKey = (key, labelsById = {}) => {
  if (key === 'legacy:in') return 'Entry';
  if (key === 'legacy:out') return 'Exit';
  if (key === 'legacy:other') return 'Other';
  if (key.startsWith('cam:')) {
    const id = key.slice(4).trim();
    if (!id) return 'Recording';
    const lb = labelsById[id] || labelsById[id.toLowerCase()];
    if (lb) return lb;
    if (id.length > 12) return `Camera ${id.slice(0, 8)}…`;
    return `Camera ${id}`;
  }
  return 'Recording';
};

/** Match RecordingInterface camera id rules (UUID normalize + ≤36 char slug). */
const normalizeConfiguredCameraId = (raw) => {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(s)) {
    return s.toLowerCase();
  }
  if (/^[0-9a-fA-F]{32}$/.test(s)) {
    const x = s.toLowerCase();
    return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20, 32)}`;
  }
  if (s.length > 36) return null;
  return s;
};

const cameraIdsMatch = (aRaw, bRaw) => {
  const a = normalizeConfiguredCameraId(aRaw) || String(aRaw || '').trim().toLowerCase();
  const b = normalizeConfiguredCameraId(bRaw) || String(bRaw || '').trim().toLowerCase();
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
};

/** Same id rules as RecordingInterface `dedupeNormalizedCameraIds` for schedule slot membership. */
const dedupeSlotCameraIds = (ids) => {
  const seen = new Set();
  const out = [];
  for (let i = 0; i < (ids?.length ?? 0); i += 1) {
    const raw = ids[i];
    let c = normalizeConfiguredCameraId(raw);
    if (!c) {
      const t = raw == null ? '' : String(raw).trim();
      if (!t || t.length > 36) continue;
      c = t;
    }
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
};

const firstCameraByType = (configuredList, typeUpper) =>
  configuredList.find((c) => String(c?.camera_type || '').toUpperCase() === typeUpper) ?? null;

/** Whether a recording row belongs to this configured camera (CAM_* id or legacy IN/OUT file). */
export const recordingBelongsToConfiguredCamera = (rec, cam, configuredList) => {
  if (!rec || !cam || !Array.isArray(configuredList)) return false;
  const recCid = rec.camera_config_id != null ? String(rec.camera_config_id).trim() : '';
  if (recCid) {
    return cameraIdsMatch(recCid, cam.id);
  }
  const ty = String(cam.camera_type || '').toUpperCase();
  const rk = recordingTrackKeyFromRec(rec);
  if (ty === 'IN' && rk === 'legacy:in') {
    const firstIn = firstCameraByType(configuredList, 'IN');
    return firstIn ? cameraIdsMatch(firstIn.id, cam.id) : false;
  }
  if (ty === 'OUT' && rk === 'legacy:out') {
    const firstOut = firstCameraByType(configuredList, 'OUT');
    return firstOut ? cameraIdsMatch(firstOut.id, cam.id) : false;
  }
  return false;
};

const accentFillForConfiguredCamera = (cam) => {
  const ty = String(cam?.camera_type || '').toUpperCase();
  if (ty === 'IN') return RECORDING_ENTRY;
  if (ty === 'OUT') return RECORDING_EXIT;
  const id = normalizeConfiguredCameraId(cam?.id) || String(cam?.id || '').trim();
  if (!id) return '#64748b';
  return recordingFillForTrackKey(`cam:${id}`);
};

/** Whether this recording row appears under the selected configured-camera tab. */
const recordingShowsOnTab = (rec, tab, configuredList) => {
  const cf = Array.isArray(configuredList) ? configuredList.filter(Boolean) : [];
  if (!rec || !tab || !tab.camera) return false;
  return recordingBelongsToConfiguredCamera(rec, tab.camera, cf);
};

/**
 * True when this recording is from a camera currently assigned as IN or OUT in `camera_config`
 * (via `camera_config_id`, legacy `camera` column, or legacy IN_/OUT_* filename when those lanes exist).
 * Used on the Recordings page player to show check-in / check-out timeline markers only on lane clips.
 */
export const recordingIsInOrOutLane = (rec, configuredList) => {
  const cf = Array.isArray(configuredList) ? configuredList.filter(Boolean) : [];
  const inCams = cf.filter((c) => String(c?.camera_type || '').toUpperCase() === 'IN');
  const outCams = cf.filter((c) => String(c?.camera_type || '').toUpperCase() === 'OUT');
  if (!inCams.length && !outCams.length) return false;

  const recCid = rec?.camera_config_id != null ? String(rec.camera_config_id).trim() : '';
  if (recCid) {
    return (
      inCams.some((c) => cameraIdsMatch(recCid, c.id)) || outCams.some((c) => cameraIdsMatch(recCid, c.id))
    );
  }

  const camCol = String(rec?.camera || '').trim().toUpperCase();
  if (camCol === 'IN' && inCams.length) return true;
  if (camCol === 'OUT' && outCams.length) return true;

  const rk = recordingTrackKeyFromRec(rec);
  if (rk === 'legacy:in' && inCams.length) return true;
  if (rk === 'legacy:out' && outCams.length) return true;
  return false;
};

/** Clock label for hour 0–23 on the 24h axis. */
const formatHour12ForAxis = (h24) => {
  if (h24 === 0) return '12 AM';
  if (h24 < 12) return `${h24} AM`;
  if (h24 === 12) return '12 PM';
  return `${h24 - 12} PM`;
};

/** Minutes from local midnight (0–1440); matches `DayTimelineBar` / attendance hover `timeLabel` style. */
const formatTime12hFromDayMinute = (totalMinutes) => {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, totalMinutes));
  const rounded = Math.min(DAY_MINUTES - 1, Math.round(clamped));
  const hours = Math.floor(rounded / 60) % 24;
  const minutes = rounded % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
};

function mergeIntervals(intervals) {
  if (!intervals.length) return [];
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const out = [];
  let [cs, ce] = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    if (s <= ce) ce = Math.max(ce, e);
    else {
      out.push([cs, ce]);
      [cs, ce] = [s, e];
    }
  }
  out.push([cs, ce]);
  return out;
}

function normalizeScheduleSlots(data) {
  const fmtHm = (hour, minute) =>
    `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  if (Array.isArray(data?.windows)) {
    return data.windows.map((w) => ({
      startTime: fmtHm(w.hour, w.minute),
      durationHours: String(w.duration_hours ?? 2),
      enabled: w.enabled !== false,
      cameraConfigIds: dedupeSlotCameraIds(Array.isArray(w.camera_config_ids) ? w.camera_config_ids : []),
    }));
  }
  return [
    {
      startTime: data?.window1_start_time ?? data?.start_time ?? '09:00',
      durationHours: String(data?.window1_duration_hours ?? data?.duration_hours ?? 2),
      enabled: true,
      cameraConfigIds: [],
    },
    {
      startTime: data?.window2_start_time ?? '14:00',
      durationHours: String(data?.window2_duration_hours ?? 2),
      enabled: true,
      cameraConfigIds: [],
    },
  ];
}

function slotToMinuteRange(startTime, durationHours) {
  const m = String(startTime || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  const startMin = h * 60 + min;
  const dur = parseFloat(durationHours);
  const add = Number.isFinite(dur) ? dur * 60 : 120;
  let endMin = startMin + add;
  endMin = Math.min(DAY_MINUTES, endMin);
  return [startMin, endMin];
}

async function fetchAllRecordingsForDay(dateKey) {
  const all = [];
  let offset = 0;
  const limit = 100;
  for (;;) {
    const params = new URLSearchParams({
      date_from: dateKey,
      date_to: dateKey,
      limit: String(limit),
      offset: String(offset),
    });
    const res = await authenticatedFetch(`/recording/recordings?${params.toString()}`);
    if (!res.ok) throw new Error('Failed to load recordings');
    const data = await res.json();
    const batch = data.recordings || [];
    all.push(...batch);
    if (!data.pagination?.has_next || batch.length === 0) break;
    offset += limit;
  }
  return all;
}

function clampZoomRange(start, end) {
  let s = Math.max(0, start);
  let e = Math.min(DAY_MINUTES, end);
  if (e - s < ZOOM_MIN_SPAN_MINS) {
    const mid = (s + e) / 2;
    s = Math.max(0, mid - ZOOM_MIN_SPAN_MINS / 2);
    e = Math.min(DAY_MINUTES, s + ZOOM_MIN_SPAN_MINS);
    s = Math.max(0, e - ZOOM_MIN_SPAN_MINS);
  }
  if (e - s > ZOOM_MAX_SPAN_MINS) {
    const mid = (s + e) / 2;
    s = Math.max(0, mid - ZOOM_MAX_SPAN_MINS / 2);
    e = s + ZOOM_MAX_SPAN_MINS;
  }
  return { visibleStartMinute: s, visibleEndMinute: e };
}

/** Minimal play dialog for attendance modal / audit logs (same player as Recordings timeline). */
export function RecordingQuickPlayDialog({
  open,
  recording,
  initialSeekSeconds,
  onClose,
  headerNotice = null,
}) {
  const [videoObjectUrl, setVideoObjectUrl] = useState(null);
  const [useHlsPlayback, setUseHlsPlayback] = useState(false);
  const [hlsManifestAbsoluteUrl, setHlsManifestAbsoluteUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const videoRef = useRef(null);

  const enforceQuickPlaySilent = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.volume = 0;
  }, []);

  useEffect(() => {
    if (!open || !recording?.id) return undefined;
    let cancelled = false;
    setVideoLoading(true);
    setVideoError(null);
    setVideoObjectUrl(null);
    setUseHlsPlayback(false);
    setHlsManifestAbsoluteUrl(null);

    (async () => {
      try {
        if (!isRecordingWebPlayable(recording)) {
          throw new Error(
            recordingTranscodingBlockedTooltip(recording) || 'Playback unavailable for this recording.',
          );
        }
        const res = await authenticatedFetch(
          `/recording/recordings/${encodeURIComponent(recording.id)}/stream-url`
        );
        const data = await res.json().catch(() => {
          throw new Error('Invalid stream URL response');
        });
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid stream URL payload');
        }
        if (cancelled) return;
        const streamToken = extractStreamTokenFromPayload(data);
        if (!streamToken) throw new Error('Missing playback token');
        if (isRecordingHlsStreamable(recording)) {
          setUseHlsPlayback(true);
          const manifestRel = `/recording/recordings/${encodeURIComponent(recording.id)}/hls/index.m3u8?token=${encodeURIComponent(streamToken)}`;
          setHlsManifestAbsoluteUrl(buildApiUrl(manifestRel));
        } else {
          setUseHlsPlayback(false);
          setVideoObjectUrl(buildApiUrl(data.url));
        }
      } catch (e) {
        if (!cancelled) setVideoError(e.message || 'Playback failed');
      } finally {
        if (!cancelled) setVideoLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, recording?.id]);

  /** Same recording, new click position: parent may not remount stream; seek existing player. */
  useEffect(() => {
    if (!open || videoLoading) return undefined;
    if (typeof initialSeekSeconds !== 'number' || !Number.isFinite(initialSeekSeconds) || initialSeekSeconds < 0) {
      return undefined;
    }
    const video = videoRef.current;
    if (!video) return undefined;
    const apply = () => {
      try {
        const d = video.duration;
        if (!Number.isFinite(d) || d <= 0) return;
        video.currentTime = Math.max(0, Math.min(initialSeekSeconds, Math.max(0, d - 0.05)));
      } catch (_) {
        /* ignore */
      }
    };
    apply();
    video.addEventListener('loadedmetadata', apply, { once: true });
    return () => video.removeEventListener('loadedmetadata', apply);
  }, [open, videoLoading, initialSeekSeconds, recording?.id]);

  useEffect(() => {
    if (!open || videoLoading || !useHlsPlayback || !hlsManifestAbsoluteUrl) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    video.muted = true;
    video.volume = 0;
    let cancelled = false;
    let hls = null;
    if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: false,
        maxBufferLength: HLS_MAX_BUFFER_SECONDS,
        maxMaxBufferLength: HLS_MAX_MAX_BUFFER_SECONDS,
        backBufferLength: HLS_BACK_BUFFER_SECONDS,
      });
      hls.loadSource(hlsManifestAbsoluteUrl);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (cancelled) return;
        if (typeof initialSeekSeconds === 'number' && Number.isFinite(initialSeekSeconds) && initialSeekSeconds >= 0) {
          try {
            const d = video.duration;
            const t = Number.isFinite(d) && d > 0 ? Math.max(0, Math.min(initialSeekSeconds, Math.max(0, d - 0.05))) : initialSeekSeconds;
            video.currentTime = t;
          } catch (_) {
            /* ignore */
          }
        }
        video.muted = true;
        video.volume = 0;
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (cancelled || !data?.fatal) return;
        setVideoError(data.details || 'HLS error');
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = hlsManifestAbsoluteUrl;
      const onMeta = () => {
        if (cancelled) return;
        if (typeof initialSeekSeconds === 'number' && Number.isFinite(initialSeekSeconds) && initialSeekSeconds >= 0) {
          try {
            const d = video.duration;
            const t = Number.isFinite(d) && d > 0 ? Math.max(0, Math.min(initialSeekSeconds, Math.max(0, d - 0.05))) : initialSeekSeconds;
            video.currentTime = t;
          } catch (_) {
            /* ignore */
          }
        }
        video.muted = true;
        video.volume = 0;
        video.play().catch(() => {});
      };
      video.addEventListener('loadedmetadata', onMeta, { once: true });
    } else {
      setVideoError('HLS not supported');
    }
    return () => {
      cancelled = true;
      if (hls) try { hls.destroy(); } catch (_) { /* ignore */ }
      try {
        video.pause();
        video.removeAttribute('src');
        video.load();
      } catch (_) { /* ignore */ }
    };
  }, [open, videoLoading, useHlsPlayback, hlsManifestAbsoluteUrl, initialSeekSeconds]);

  useEffect(() => {
    if (!open || videoLoading) return undefined;
    if (!videoObjectUrl && !hlsManifestAbsoluteUrl) return undefined;
    enforceQuickPlaySilent();
    return undefined;
  }, [open, videoLoading, videoObjectUrl, hlsManifestAbsoluteUrl, recording?.id, enforceQuickPlaySilent]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
        {recording?.filename || 'Recording'}
      </DialogTitle>
      <DialogContent>
        {headerNotice ? (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {headerNotice}
          </Alert>
        ) : null}
        {videoLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {videoError && <Alert severity="error">{videoError}</Alert>}
        {!videoLoading && (videoObjectUrl || hlsManifestAbsoluteUrl) && (
          <Box
            component="video"
            ref={videoRef}
            muted
            onPlay={enforceQuickPlaySilent}
            onVolumeChange={enforceQuickPlaySilent}
            controls
            playsInline
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              v.muted = true;
              v.volume = 0;
              if (useHlsPlayback) return;
              if (typeof initialSeekSeconds !== 'number' || !Number.isFinite(initialSeekSeconds) || initialSeekSeconds < 0) return;
              try {
                const d = v.duration;
                const t = Number.isFinite(d) && d > 0 ? Math.max(0, Math.min(initialSeekSeconds, Math.max(0, d - 0.05))) : initialSeekSeconds;
                v.currentTime = t;
              } catch (_) {
                /* ignore */
              }
            }}
            sx={{ width: '100%', maxHeight: '60vh', borderRadius: 1, bgcolor: '#000' }}
            src={useHlsPlayback ? undefined : videoObjectUrl}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * 24h recording timeline: schedule slots (light green) + recordings (dark green entry / blue exit).
 * @param {object} props
 * @param {string} props.dateKey - yyyy-MM-dd
 * @param {string} [props.dateLabel]
 * @param {(rec: object, opts?: { seekSeconds?: number }) => void} [props.onPlayRecording] - Parent play modal; optional seek into clip.
 * @param {() => void} [props.onDeleteDay] - Optional: confirm + delete every recording for this calendar day (Recordings page).
 * @param {object[]|undefined} [props.recordingsFromPage] - If set, only these rows are drawn (no per-day fetch). Omit for attendance modal (fetch full day).
 * @param {object[]} [props.configuredCameras] - Cameras from /camera-streams (same list as Recording page schedule). One timeline tab each; labels from Camera Label.
 * @param {boolean} [props.embedded] - Lighter wrapper for use inside attendance modal.
 */
export default function RecordingDayTimeline({
  dateKey,
  dateLabel,
  onPlayRecording,
  onDeleteDay,
  recordingsFromPage,
  configuredCameras = [],
  embedded = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const usePagedRecordings = recordingsFromPage !== undefined;
  const [activeTabKey, setActiveTabKey] = useState('');
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [scheduleError, setScheduleError] = useState(null);
  const [fetchedRecordings, setFetchedRecordings] = useState([]);
  const [recordingsLoading, setRecordingsLoading] = useState(!usePagedRecordings);
  const [recordingsError, setRecordingsError] = useState(null);
  const [zoomState, setZoomState] = useState(null);
  const [hoverRec, setHoverRec] = useState(null);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  /** @type {{ percent: number, timeLabel: string } | null} */
  const [timelineHoverInfo, setTimelineHoverInfo] = useState(null);
  /** @type {[{ recording: object, seekSeconds?: number } | null, function]} */
  const [internalPlay, setInternalPlay] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const barRef = useRef(null);
  const barContainerRef = useRef(null);
  const panRef = useRef({ active: false, startX: 0, startStart: 0, startEnd: 0 });
  const panMovedRef = useRef(false);
  const lastWheelRef = useRef(0);
  const zoomStateRef = useRef(null);
  zoomStateRef.current = zoomState;

  const startM = 0;
  const endM = DAY_MINUTES;
  const effectiveStart = zoomState ? zoomState.visibleStartMinute : startM;
  const effectiveEnd = zoomState ? zoomState.visibleEndMinute : endM;
  const effectiveSpan = effectiveEnd - effectiveStart;

  const recordingsData = usePagedRecordings ? recordingsFromPage : fetchedRecordings;

  /** One tab per configured camera that has ≥1 recording on this `dateKey` (same match rules as the bar). */
  const timelineTabs = useMemo(() => {
    const cf = Array.isArray(configuredCameras) ? configuredCameras.filter(Boolean) : [];
    if (cf.length === 0) return [];
    const CAMERA_TAB_ORDER = { IN: 0, OUT: 1, ACTIVITY: 2 };
    const ranked = [...cf].sort((a, b) => {
      const ta = String(a.camera_type || '').toUpperCase();
      const tb = String(b.camera_type || '').toUpperCase();
      const ra = CAMERA_TAB_ORDER[ta] ?? 99;
      const rb = CAMERA_TAB_ORDER[tb] ?? 99;
      if (ra !== rb) return ra - rb;
      const la = (a.label || a.name || '').trim();
      const lb = (b.label || b.name || '').trim();
      return la.localeCompare(lb, undefined, { sensitivity: 'base' });
    });
    const allTabs = ranked.map((cam) => {
      const nid = normalizeConfiguredCameraId(cam.id) || String(cam.id || '').trim();
      const tabKey = `cfg:${nid || String(cam.id)}`;
      const lb = (cam.label || cam.name || '').trim();
      return {
        tabKey,
        camera: cam,
        label: lb || `Camera ${String(cam.id).slice(0, 8)}…`,
        accent: accentFillForConfiguredCamera(cam),
      };
    });
    if (recordingsLoading && !usePagedRecordings) return allTabs;
    return allTabs.filter((tab) =>
      recordingsData.some((rec) => {
        const dk = localDateKeyFromInstant(rec.recorded_at || rec.start_time);
        if (dk !== dateKey) return false;
        return recordingShowsOnTab(rec, tab, cf);
      }),
    );
  }, [configuredCameras, recordingsData, dateKey, recordingsLoading, usePagedRecordings]);

  const resolvedActiveTabKey = useMemo(() => {
    if (!timelineTabs.length) return '';
    if (activeTabKey && timelineTabs.some((t) => t.tabKey === activeTabKey)) return activeTabKey;
    return timelineTabs[0].tabKey;
  }, [timelineTabs, activeTabKey]);

  /** Light-green slot bands: only for windows that include the active tab's camera. */
  const slotRanges = useMemo(() => {
    const tab = timelineTabs.find((t) => t.tabKey === resolvedActiveTabKey);
    const activeCameraId = tab?.camera?.id;
    const raw = [];
    for (const s of scheduleSlots) {
      if (s.enabled === false) continue;
      const ids = dedupeSlotCameraIds(s.cameraConfigIds);
      if (!ids.length) continue;
      if (activeCameraId == null || !ids.some((cid) => cameraIdsMatch(cid, activeCameraId))) continue;
      const r = slotToMinuteRange(s.startTime, s.durationHours);
      if (r) raw.push(r);
    }
    return mergeIntervals(raw);
  }, [scheduleSlots, timelineTabs, resolvedActiveTabKey]);

  useEffect(() => {
    if (!timelineTabs.length) return;
    if (!timelineTabs.some((t) => t.tabKey === activeTabKey)) {
      setActiveTabKey(timelineTabs[0].tabKey);
    }
  }, [timelineTabs, activeTabKey]);

  const recordingsForActiveTab = useMemo(() => {
    const cf = Array.isArray(configuredCameras) ? configuredCameras.filter(Boolean) : [];
    const tab = timelineTabs.find((t) => t.tabKey === resolvedActiveTabKey);
    return recordingsData.filter((rec) => {
      const dk = localDateKeyFromInstant(rec.recorded_at || rec.start_time);
      if (dk !== dateKey) return false;
      if (!resolvedActiveTabKey) return false;
      if (!tab) return false;
      return recordingShowsOnTab(rec, tab, cf);
    });
  }, [recordingsData, dateKey, resolvedActiveTabKey, timelineTabs, configuredCameras]);

  const activeTrackFill = useMemo(() => {
    const t = timelineTabs.find((x) => x.tabKey === resolvedActiveTabKey);
    return t?.accent ?? RECORDING_ENTRY;
  }, [timelineTabs, resolvedActiveTabKey]);

  const recordingSegments = useMemo(() => {
    return recordingsForActiveTab
      .map((rec) => {
        const startIso = rec.recorded_at || rec.start_time;
        const startRaw =
          minutesIntoLocalCalendarDay(startIso, dateKey) ?? parseFilenameStartMinutes(rec.filename, dateKey);
        if (startRaw == null) return null;
        const durSec = rec.duration_seconds != null ? Number(rec.duration_seconds) : 0;
        let endRaw;
        if (rec.end_time) {
          endRaw = minutesIntoLocalCalendarDay(rec.end_time, dateKey);
          if (endRaw == null || endRaw < startRaw) {
            endRaw = startRaw + durSec / 60;
          }
        } else {
          endRaw = startRaw + durSec / 60;
        }
        const startMin = Math.max(0, startRaw);
        const endMin = Math.min(DAY_MINUTES, Math.max(startMin, endRaw));
        if (endMin <= 0 || startMin >= DAY_MINUTES) return null;
        return { rec, startMin, endMin };
      })
      .filter(Boolean)
      .sort((a, b) => a.startMin - b.startMin);
  }, [recordingsForActiveTab, dateKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setScheduleLoading(true);
      setScheduleError(null);
      try {
        const res = await authenticatedFetch('/recording/schedule');
        if (!res.ok) throw new Error('Failed to load schedule');
        const data = await res.json();
        if (!cancelled) setScheduleSlots(normalizeScheduleSlots(data));
      } catch (e) {
        if (!cancelled) setScheduleError(e.message || 'Schedule error');
      } finally {
        if (!cancelled) setScheduleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (usePagedRecordings || !dateKey) {
      if (usePagedRecordings) setRecordingsLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setRecordingsLoading(true);
      setRecordingsError(null);
      try {
        const all = await fetchAllRecordingsForDay(dateKey);
        if (!cancelled) setFetchedRecordings(all);
      } catch (e) {
        if (!cancelled) {
          setRecordingsError(e.message || 'Recordings error');
          setFetchedRecordings([]);
        }
      } finally {
        if (!cancelled) setRecordingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dateKey, usePagedRecordings]);

  const minuteToPercent = useCallback(
    (minute) => ((minute - effectiveStart) / effectiveSpan) * 100,
    [effectiveStart, effectiveSpan]
  );

  /** Start (track color) / end (black) edge dots; same visual % merges to a half-and-half dot. */
  const edgeMarkers = useMemo(() => {
    const trackColor = activeTrackFill;
    const groups = new Map();
    for (const { rec, startMin, endMin } of recordingSegments) {
      const vs = Math.max(startMin, effectiveStart);
      const ve = Math.min(endMin, effectiveEnd);
      if (ve <= vs) continue;
      for (const [min, isStart] of [
        [vs, true],
        [ve, false],
      ]) {
        const pct = minuteToPercent(min);
        const key = pct.toFixed(EDGE_MARKER_PCT_DECIMALS);
        let g = groups.get(key);
        if (!g) {
          g = { leftPct: pct, hasStart: false, hasEnd: false, startRecording: null };
          groups.set(key, g);
        }
        if (isStart) {
          g.hasStart = true;
          g.startRecording = rec;
        } else {
          g.hasEnd = true;
        }
      }
    }
    const out = [];
    for (const g of groups.values()) {
      let variant = 'start';
      if (g.hasStart && g.hasEnd) variant = 'split';
      else if (g.hasEnd) variant = 'end';
      out.push({
        key: `${g.leftPct}-${variant}`,
        leftPct: g.leftPct,
        variant,
        trackColor,
        startRecording: g.startRecording || null,
      });
    }
    out.sort((a, b) => a.leftPct - b.leftPct);
    return out;
  }, [recordingSegments, effectiveStart, effectiveEnd, minuteToPercent, activeTrackFill]);

  const openPlay = useCallback(
    (rec, opts = {}) => {
      if (!isRecordingWebPlayable(rec)) return;
      if (typeof onPlayRecording === 'function') {
        onPlayRecording(rec, opts);
      } else {
        const sk = opts?.seekSeconds;
        setInternalPlay({
          recording: rec,
          seekSeconds:
            typeof sk === 'number' && Number.isFinite(sk) && sk >= 0 ? sk : undefined,
        });
      }
    },
    [onPlayRecording]
  );

  const seekAtClientXForSegment = useCallback(
    (clientX, seg) => {
      const rect = barRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;
      const rel = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const minute = effectiveStart + rel * (effectiveEnd - effectiveStart);
      const offsetSeconds = Math.max(0, (minute - seg.startMin) * 60);
      openPlay(seg.rec, { seekSeconds: offsetSeconds });
    },
    [effectiveStart, effectiveEnd, openPlay]
  );

  const handleBarSeekClick = useCallback(
    (clientX) => {
      const rect = barRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;
      const rel = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const minute = effectiveStart + rel * (effectiveEnd - effectiveStart);
      const seg = pickRecordingSegmentAtMinute(recordingSegments, minute);
      if (!seg) return;
      seekAtClientXForSegment(clientX, seg);
    },
    [effectiveStart, effectiveEnd, recordingSegments, seekAtClientXForSegment]
  );

  const handleTimelineHoverMove = useCallback(
    (e) => {
      if (isPanning) return;
      const rect = barRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;
      const rel = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const hoverMinute = effectiveStart + rel * (effectiveEnd - effectiveStart);
      setTimelineHoverInfo({
        percent: rel * 100,
        timeLabel: formatTime12hFromDayMinute(hoverMinute),
      });
    },
    [effectiveStart, effectiveEnd, isPanning]
  );

  const handleTimelineHoverLeave = useCallback(() => {
    setTimelineHoverInfo(null);
  }, []);

  const clampZoomRangeCb = useCallback((s, e) => clampZoomRange(s, e), []);

  const handleWheel = useCallback((e) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (e.deltaY === 0) return;
    const now = Date.now();
    if (now - lastWheelRef.current < 20) return;
    lastWheelRef.current = now;

    setZoomState((prev) => {
      const curStart = prev ? prev.visibleStartMinute : startM;
      const curEnd = prev ? prev.visibleEndMinute : endM;
      const curSpan = curEnd - curStart;
      const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const pointerMinute = curStart + relX * curSpan;
      const factor = e.deltaY > 0 ? ZOOM_WHEEL_FACTOR : 1 / ZOOM_WHEEL_FACTOR;
      let newSpan = curSpan * factor;
      newSpan = Math.max(ZOOM_MIN_SPAN_MINS, Math.min(ZOOM_MAX_SPAN_MINS, newSpan));
      const f = curSpan > 0 ? (pointerMinute - curStart) / curSpan : 0.5;
      let newStart = pointerMinute - f * newSpan;
      let newEnd = newStart + newSpan;
      return clampZoomRangeCb(newStart, newEnd);
    });
  }, [clampZoomRangeCb]);

  useEffect(() => {
    const el = barContainerRef.current;
    if (!el) return undefined;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const handlePanStart = useCallback((clientX) => {
    if (!zoomState) return;
    panMovedRef.current = false;
    setTimelineHoverInfo(null);
    setIsPanning(true);
    panRef.current = {
      active: true,
      startX: clientX,
      startStart: zoomState.visibleStartMinute,
      startEnd: zoomState.visibleEndMinute,
    };
  }, [zoomState]);

  const handlePanMove = useCallback((clientX) => {
    if (!panRef.current.active) return;
    if (Math.abs(clientX - panRef.current.startX) > 5) {
      panMovedRef.current = true;
    }
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect) return;
    const deltaX = clientX - panRef.current.startX;
    const span = panRef.current.startEnd - panRef.current.startStart;
    const deltaMinute = (deltaX / rect.width) * span;
    let newStart = panRef.current.startStart - deltaMinute;
    let newEnd = panRef.current.startEnd - deltaMinute;
    setZoomState(clampZoomRange(newStart, newEnd));
  }, []);

  const handlePanEnd = useCallback(() => {
    panRef.current.active = false;
    setIsPanning(false);
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (zoomState) {
        e.preventDefault();
        handlePanStart(e.clientX);
      }
    },
    [zoomState, handlePanStart]
  );

  useEffect(() => {
    const onMouseMove = (e) => handlePanMove(e.clientX);
    const onMouseUp = () => handlePanEnd();
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [handlePanMove, handlePanEnd]);

  const handleDoubleClick = useCallback(() => setZoomState(null), []);

  const handleZoomStep = useCallback((direction) => {
    setZoomState((prev) => {
      const curStart = prev ? prev.visibleStartMinute : startM;
      const curEnd = prev ? prev.visibleEndMinute : endM;
      const curSpan = curEnd - curStart;
      const zoomFactor = direction === 'in' ? 1 / 1.2 : 1.2;
      let nextSpan = curSpan * zoomFactor;
      nextSpan = Math.max(ZOOM_MIN_SPAN_MINS, Math.min(ZOOM_MAX_SPAN_MINS, nextSpan));
      if (nextSpan >= ZOOM_MAX_SPAN_MINS - 1) return null;
      const center = (curStart + curEnd) / 2;
      return clampZoomRange(center - nextSpan / 2, center + nextSpan / 2);
    });
  }, []);

  const currentZoomSpan = zoomState ? zoomState.visibleEndMinute - zoomState.visibleStartMinute : ZOOM_MAX_SPAN_MINS;
  const disableZoomIn = currentZoomSpan <= ZOOM_MIN_SPAN_MINS + 1;
  const disableZoomOut = currentZoomSpan >= ZOOM_MAX_SPAN_MINS - 1;
  const zoomPercent = `${Math.round((ZOOM_MAX_SPAN_MINS / currentZoomSpan) * 100)}%`;

  /** Every hour 12 AM … 11 PM on the visible span; plus 11:59 PM at day end when in view. */
  const displayHourLabels = useMemo(() => {
    const labels = [];
    const hourStep = isMobile ? 4 : 1;
    for (let h = 0; h < 24; h++) {
      if (h % hourStep !== 0) continue;
      const m = h * 60;
      if (m < effectiveStart - 50 || m > effectiveEnd + 50) continue;
      const pos = minuteToPercent(m);
      if (pos < -12 || pos > 112) continue;
      labels.push({
        key: `hour-${h}`,
        label: formatHour12ForAxis(h),
        position: pos,
      });
    }
    const endM = DAY_MINUTES - 1;
    if (!isMobile && endM >= effectiveStart - 40 && endM <= effectiveEnd + 40) {
      const endPos = minuteToPercent(endM);
      if (endPos >= -8 && endPos <= 108) {
        labels.push({ key: 'end-2359', label: '11:59 PM', position: endPos });
      }
    }
    return labels;
  }, [effectiveStart, effectiveEnd, minuteToPercent, isMobile]);

  const label = dateLabel || (dateKey ? format(parseISO(`${dateKey}T12:00:00`), 'EEE, MMM dd, yyyy') : '');

  const body = (
    <Box sx={{ mt: embedded ? 1.5 : 0 }}>
      <Typography
        variant="h6"
        component="div"
        sx={{
          fontWeight: 700,
          color: 'primary.main',
          fontSize: { xs: '1.05rem', sm: '1.2rem' },
          lineHeight: 1.2,
          mb: 1,
        }}
      >
        {label}
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{
          mb: 1,
          gap: 1,
          width: '100%',
          minHeight: { xs: 'auto', sm: 34 },
          overflowX: { xs: 'visible', sm: 'auto' },
          flexShrink: 0,
          pb: 0.25,
          '&::-webkit-scrollbar': { height: 6 },
        }}
      >
        {timelineTabs.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {Array.isArray(configuredCameras) && configuredCameras.filter(Boolean).length > 0
              ? 'No recordings for this day.'
              : 'No cameras configured — add cameras under Camera Streams.'}
          </Typography>
        ) : (
          <ToggleButtonGroup
            size="small"
            value={resolvedActiveTabKey}
            exclusive
            onChange={(_, v) => v && setActiveTabKey(v)}
            sx={{
              flexShrink: 0,
              '& .MuiToggleButton-root': { py: 0.25, px: 1.25, textTransform: 'none', fontWeight: 600 },
              width: { xs: '100%', sm: 'auto' },
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-start' },
            }}
          >
            {timelineTabs.map((t) => (
              <ToggleButton
                key={t.tabKey}
                value={t.tabKey}
              >
                {t.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Box sx={{ width: 12, height: 8, bgcolor: SLOT_GREEN, borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Slot
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              width: 12,
              height: 8,
              bgcolor: activeTrackFill,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Recording
          </Typography>
        </Stack>

        <Box sx={{ flex: 1, minWidth: 4, display: { xs: 'none', sm: 'block' } }} aria-hidden />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-end' }, gap: 0.5, flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              px: 0.4,
              py: 0.2,
              borderRadius: 2,
              border: '1px solid #cbd5e1',
              bgcolor: '#f8fafc',
            }}
          >
            <Tooltip title="Zoom out" arrow>
              <span>
                <IconButton size="small" onClick={() => handleZoomStep('out')} disabled={disableZoomOut} sx={{ width: 26, height: 26 }}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Chip size="small" label={zoomPercent} sx={{ height: 24, minWidth: 58, fontSize: '0.7rem', fontWeight: 700 }} />
            <Tooltip title="Zoom in" arrow>
              <span>
                <IconButton size="small" onClick={() => handleZoomStep('in')} disabled={disableZoomIn} sx={{ width: 26, height: 26 }}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Reset zoom" arrow>
              <span>
                <IconButton size="small" onClick={() => setZoomState(null)} disabled={!zoomState} sx={{ width: 26, height: 26 }}>
                  <RestartAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {typeof onDeleteDay === 'function' && (
            <Tooltip title="Delete all recordings for this day" arrow>
              <IconButton
                type="button"
                size="small"
                color="error"
                onClick={onDeleteDay}
                aria-label="Delete all recordings for this day"
                sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: 1 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Stack>

      <Box
        ref={barContainerRef}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        sx={{
          position: 'relative',
          mb: 0.5,
          touchAction: 'none',
          userSelect: 'none',
          cursor: zoomState ? (isPanning ? 'grabbing' : 'grab') : 'default',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{ mb: { xs: 1.25, sm: 0.5 } }}
        >
          <Typography
            variant="caption"
            sx={{ width: { xs: '100%', sm: TRACK_SIDE_LABEL_WIDTH_PX }, flexShrink: 0, fontWeight: 600, color: 'text.secondary' }}
          >
            {timelineTabs.find((t) => t.tabKey === resolvedActiveTabKey)?.label ?? 'Camera'}
          </Typography>
          <Box
            ref={barRef}
            onMouseMove={handleTimelineHoverMove}
            onMouseLeave={handleTimelineHoverLeave}
            onClick={(e) => {
              if (panMovedRef.current) {
                panMovedRef.current = false;
                return;
              }
              handleBarSeekClick(e.clientX);
            }}
            sx={{
              position: 'relative',
              flex: 1,
              minWidth: 0,
              width: { xs: '100%', sm: 0 },
              height: 40,
              overflow: 'visible',
              cursor: isPanning ? 'grabbing' : zoomState ? 'grab' : 'pointer',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: 16,
                transform: 'translateY(-50%)',
                bgcolor: TRACK_GREY,
                borderRadius: 8,
                overflow: 'hidden',
                zIndex: 0,
              }}
            >
              {/* Hour dividers at 1:00 … 11:00 (visible window only) */}
              {Array.from({ length: 23 }, (_, i) => {
                const h = i + 1;
                const m = h * 60;
                if (m <= effectiveStart || m >= effectiveEnd) return null;
                const pos = minuteToPercent(m);
                if (pos <= 0.2 || pos >= 99.8) return null;
                return (
                  <Box
                    key={`hour-grid-${h}`}
                    sx={{
                      position: 'absolute',
                      left: `${pos}%`,
                      top: 0,
                      bottom: 0,
                      width: 0,
                      borderLeft: '1px solid',
                      borderColor: 'rgba(15, 23, 42, 0.08)',
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  />
                );
              })}
              {slotRanges.map(([s, e], i) => {
                const ls = Math.max(s, effectiveStart);
                const le = Math.min(e, effectiveEnd);
                if (le <= ls) return null;
                const p0 = minuteToPercent(ls);
                const p1 = minuteToPercent(le);
                return (
                  <Box
                    key={`slot-${i}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: `${p0}%`,
                      width: `${p1 - p0}%`,
                      height: '100%',
                      bgcolor: SLOT_GREEN,
                    }}
                  />
                );
              })}
              {recordingSegments.map(({ rec, startMin, endMin }, segIdx) => {
                const ls = Math.max(startMin, effectiveStart);
                const le = Math.min(endMin, effectiveEnd);
                if (le <= ls) return null;
                const p0 = minuteToPercent(ls);
                const p1 = minuteToPercent(le);
                const fill = activeTrackFill;
                const playable = isRecordingWebPlayable(rec);
                const tip =
                  recordingTranscodingBlockedTooltip(rec) ||
                  (playable ? 'Click to play' : 'Playback unavailable');
                return (
                  <Tooltip key={rec.id} title={tip} arrow>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        if (panMovedRef.current) {
                          panMovedRef.current = false;
                          return;
                        }
                        if (!playable) return;
                        seekAtClientXForSegment(e.clientX, {
                          rec,
                          startMin,
                          endMin,
                        });
                      }}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: `${p0}%`,
                        width: `${p1 - p0}%`,
                        height: '100%',
                        bgcolor: fill,
                        opacity: playable ? 0.92 : 0.45,
                        // Later recordings stack above earlier ones so clicks hit the correct clip when spans overlap.
                        zIndex: 2 + segIdx,
                        cursor: playable ? 'pointer' : 'not-allowed',
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Box>
            {edgeMarkers.map(({ key, leftPct, variant, trackColor: tc, startRecording }) => {
              const showFileTooltip = Boolean(startRecording);
              const dotSx = {
                position: 'absolute',
                left: `${leftPct}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 18,
                height: 18,
                borderRadius: '50%',
                zIndex: 6,
                pointerEvents: showFileTooltip ? 'auto' : 'none',
                cursor: showFileTooltip ? 'pointer' : 'default',
                boxSizing: 'border-box',
                border: '2px solid #ffffff',
              };
              if (variant === 'split') {
                dotSx.background = `linear-gradient(90deg, ${RECORDING_END_DOT} 50%, ${tc} 50%)`;
              } else if (variant === 'end') {
                dotSx.bgcolor = RECORDING_END_DOT;
              } else {
                dotSx.bgcolor = tc;
              }
              return (
                <Box
                  key={key}
                  component="span"
                  aria-hidden={!showFileTooltip}
                  onMouseEnter={
                    showFileTooltip
                      ? (e) => {
                          setTimelineHoverInfo(null);
                          setHoverRec(startRecording);
                          setTooltipAnchor(e.currentTarget);
                        }
                      : undefined
                  }
                  onMouseLeave={
                    showFileTooltip
                      ? () => {
                          setHoverRec(null);
                          setTooltipAnchor(null);
                        }
                      : undefined
                  }
                  sx={dotSx}
                />
              );
            })}

            {timelineHoverInfo && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: `${timelineHoverInfo.percent}%`,
                  transform: 'translateX(-50%)',
                  height: '100%',
                  zIndex: 5,
                  pointerEvents: 'none',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    top: 0,
                    bottom: 0,
                    width: 0,
                    borderLeft: '1px dashed rgb(198, 205, 214)',
                    zIndex: 1499,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    bottom: '100%',
                    mb: 0.5,
                    transform:
                      timelineHoverInfo.percent < 20
                        ? 'translate(0, 0)'
                        : timelineHoverInfo.percent > 95
                          ? 'translate(-100%, 0)'
                          : 'translate(-50%, 0)',
                    zIndex: 1500,
                    bgcolor: '#ffffff',
                    color: '#0f172a',
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
                    minWidth: 140,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#0f172a' }}>
                    {timelineHoverInfo.timeLabel}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Stack>

        <Popper
          open={Boolean(tooltipAnchor && hoverRec)}
          anchorEl={tooltipAnchor}
          placement="top"
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
          sx={{ zIndex: 2000 }}
        >
          {hoverRec && <RecordingHoverCard recording={hoverRec} accentColor={activeTrackFill} />}
        </Popper>
      </Box>

      {/* Same row geometry as the bar: spacer + flex track so % positions match segments */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25, mt: { xs: 0.5, sm: 0 } }}>
        <Box sx={{ width: { xs: 0, sm: TRACK_SIDE_LABEL_WIDTH_PX }, flexShrink: 0, display: { xs: 'none', sm: 'block' } }} aria-hidden />
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minWidth: 0,
            minHeight: { xs: 24, sm: 20 },
            height: { xs: 24, sm: 20 },
          }}
        >
          {displayHourLabels.map((item) => (
            <Typography
              key={item.key}
              variant="caption"
              sx={{
                position: 'absolute',
                left: `${item.position}%`,
                transform: 'translateX(-50%)',
                top: { xs: 2, sm: 0 },
                color: '#6b7280',
                fontSize: { xs: '0.5rem', sm: '0.52rem', md: '0.58rem' },
                fontWeight: 500,
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              {item.label}
            </Typography>
          ))}
        </Box>
      </Stack>

      {zoomState && (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              const span = effectiveEnd - effectiveStart;
              const panAmount = span * 0.2;
              const newStart = Math.max(startM, effectiveStart - panAmount);
              setZoomState(clampZoomRange(newStart, newStart + span));
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Drag bar to pan · scroll to zoom
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              const span = effectiveEnd - effectiveStart;
              const panAmount = span * 0.2;
              const newEnd = Math.min(endM, effectiveEnd + panAmount);
              setZoomState(clampZoomRange(newEnd - span, newEnd));
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <Box sx={{ width: { xs: 0, sm: TRACK_SIDE_LABEL_WIDTH_PX }, flexShrink: 0, display: { xs: 'none', sm: 'block' } }} aria-hidden />
        <Box sx={{ flex: 1, minWidth: 0, height: 6, borderRadius: 1, bgcolor: '#f1f5f9', position: 'relative', overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${(effectiveStart / DAY_MINUTES) * 100}%`,
              width: `${(effectiveSpan / DAY_MINUTES) * 100}%`,
              bgcolor: 'primary.main',
              opacity: 0.35,
              borderRadius: 1,
            }}
          />
        </Box>
      </Stack>

      {(scheduleError || recordingsError) && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {scheduleError || recordingsError}
        </Alert>
      )}
      {(scheduleLoading || recordingsLoading) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption" color="text.secondary">Loading recording data…</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {embedded ? body : (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fff' }}>
          {body}
        </Paper>
      )}
      {!onPlayRecording && (
        <RecordingQuickPlayDialog
          open={!!internalPlay}
          recording={internalPlay?.recording}
          initialSeekSeconds={internalPlay?.seekSeconds}
          onClose={() => setInternalPlay(null)}
        />
      )}
    </>
  );
}

function RecordingHoverCard({ recording, accentColor }) {
  const start = recording.recorded_at || recording.start_time;
  const end = recording.end_time;
  const fill = accentColor || RECORDING_ENTRY;
  const startD = parseRecordingDate(start);
  const endD = parseRecordingDate(end);
  const transcodeTip = recordingTranscodingBlockedTooltip(recording);
  return (
    <Paper
      elevation={8}
      sx={{
        p: 1.5,
        minWidth: 240,
        maxWidth: 320,
        borderRadius: 2,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <PlayArrowIcon sx={{ fontSize: 18, color: fill }} />
          <Typography variant="caption" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {recording.filename}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {startD && format(startD, 'h:mm a')}
          {endD && ` — ${format(endD, 'h:mm a')}`}
          {' · '}
          {formatDurationHuman(recording.duration_seconds)}
          {' · '}
          {formatSize(recording.size_bytes)}
        </Typography>
        {transcodeTip && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
            {transcodeTip}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
